// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React, { useMemo } from 'react';
import { Modal } from '@loa/ui-components';
import type { ProfileRequestDTO } from '../../types/profileRequest';
import type { Policy } from '../../types/policy';
import { deriveDisplayStatus } from './RequestDisplayStatusBadge';
import { OutstandingRequestsTable } from './OutstandingRequestsTable';
import type { NudgeMode } from './OutstandingRequestsTable';

export type { NudgeMode } from './OutstandingRequestsTable';

export interface ChooseRequestModalProps {
  open: boolean;
  initialMode: NudgeMode;
  requests: ProfileRequestDTO[];
  policyById: Map<string, Policy>;
  onClose: () => void;
  onSelect: (request: ProfileRequestDTO, mode: NudgeMode) => void;
}

/**
 * "Choose a request" chooser. Lists every nudgeable (PENDING_CLIENT /
 * PENDING_PROVIDER) request so the advisor sees the full picture, but only
 * the rows matching the entry mode (the menu option that opened it) are
 * selectable — the other group is shown dimmed and is not clickable.
 * Selecting an active row hands the request + mode back to the caller, which
 * navigates to the appropriate email page.
 */
export const ChooseRequestModal: React.FC<ChooseRequestModalProps> = ({
  open,
  initialMode,
  requests,
  policyById,
  onClose,
  onSelect,
}) => {
  // Only nudgeable rows are shown; the selectable group is fixed to the entry
  // point — no in-modal toggle.
  const nudgeable = useMemo(
    () =>
      requests.filter((r) => {
        const status = deriveDisplayStatus(r.status, r.actions);
        return status === 'PENDING_CLIENT' || status === 'PENDING_PROVIDER';
      }),
    [requests],
  );

  return (
    <Modal open={open} onClose={onClose} size="xlarge">
      <Modal.Header title="Choose a request" onClose={onClose} />
      <Modal.Content>
        <OutstandingRequestsTable
          requests={nudgeable}
          policyById={policyById}
          activeMode={initialMode}
          onRowSelect={onSelect}
          testId="choose-request-modal"
          rowTestIdPrefix="choose-request-row-"
        />
      </Modal.Content>
      <Modal.Actions secondaryAction={{ label: 'Cancel', onClick: onClose }} />
    </Modal>
  );
};
